import { midiToFreq, type AudioEngine } from './engine';
import { chordToMidi, heldSteps, noteToMidi, tokens } from './notation';
import { SONGS, type Song } from './songs';

interface Prepared {
  song: Song;
  lead: string[];
  bass: string[];
  chords: string[];
  drums: string[];
  length: number;
}

/**
 * Lookahead step sequencer ("A Tale of Two Clocks" pattern): a JS timer
 * schedules notes slightly ahead on the audio clock so timing stays tight.
 */
export class Music {
  private current: Prepared | null = null;
  private step = 0;
  private nextTime = 0;
  private timer: ReturnType<typeof setInterval> | null = null;
  private readonly waves = new Map<string, PeriodicWave>();
  private readonly prepared = new Map<string, Prepared>();
  onSongChange: ((song: Song) => void) | null = null;

  constructor(private readonly engine: AudioEngine) {}

  get song(): Song | null {
    return this.current?.song ?? null;
  }

  play(id: string): void {
    const song = SONGS[id];
    if (!song || !this.engine.ctx) return;
    if (this.current?.song.id === id && this.timer) return;
    this.current = this.prepare(song);
    this.step = 0;
    this.nextTime = this.engine.now + 0.08;
    this.onSongChange?.(song);
    if (!this.timer) this.timer = setInterval(() => this.tick(), 25);
  }

  stop(): void {
    if (this.timer) clearInterval(this.timer);
    this.timer = null;
    this.current = null;
  }

  private prepare(song: Song): Prepared {
    const hit = this.prepared.get(song.id);
    if (hit) return hit;
    const lead = tokens(song.lead);
    const bass = tokens(song.bass);
    const chords = tokens(song.chords);
    const drums = tokens(song.drums);
    const length = Math.max(lead.length, bass.length, chords.length, drums.length);
    const p = { song, lead, bass, chords, drums, length };
    this.prepared.set(song.id, p);
    return p;
  }

  private tick(): void {
    const ctx = this.engine.ctx;
    const cur = this.current;
    if (!ctx || !cur || ctx.state !== 'running') return;
    const stepDur = 60 / cur.song.bpm / 4;
    // If the tab stalled, don't try to catch up with a burst of notes.
    if (this.nextTime < ctx.currentTime - 0.2) this.nextTime = ctx.currentTime + 0.05;
    while (this.nextTime < ctx.currentTime + 0.12) {
      const swing = this.step % 2 === 1 ? cur.song.swing * stepDur : 0;
      this.scheduleStep(cur, this.step, this.nextTime + swing, stepDur);
      this.nextTime += stepDur;
      this.step = (this.step + 1) % cur.length;
    }
  }

  private scheduleStep(p: Prepared, i: number, t: number, stepDur: number): void {
    const eerie = p.song.eerie === true;
    const lead = p.lead[i % p.lead.length];
    if (lead && lead !== '-' && lead !== '.') {
      const m = noteToMidi(lead);
      if (m !== null)
        this.voice(
          p.song.leadWave ?? 'pulse25',
          midiToFreq(m),
          t,
          heldSteps(p.lead, i % p.lead.length) * stepDur * 0.92,
          eerie ? 0.07 : 0.085,
          eerie,
        );
    }
    const bass = p.bass[i % p.bass.length];
    if (bass && bass !== '-' && bass !== '.') {
      const m = noteToMidi(bass);
      if (m !== null)
        this.voice(
          'triangle',
          midiToFreq(m),
          t,
          heldSteps(p.bass, i % p.bass.length) * stepDur * 0.9,
          eerie ? 0.3 : 0.24,
          false,
        );
    }
    const chord = p.chords[i % p.chords.length];
    if (chord && chord !== '-' && chord !== '.') {
      const notes = chordToMidi(chord);
      if (notes)
        for (const n of notes)
          this.voice(
            'pulse12',
            midiToFreq(n),
            t,
            eerie ? stepDur * 8 : 0.13,
            eerie ? 0.03 : 0.028,
            eerie,
          );
    }
    const drum = p.drums[i % p.drums.length];
    if (drum && drum !== '.') this.drum(drum, t);
  }

  private wave(kind: 'pulse25' | 'pulse12'): PeriodicWave | null {
    const ctx = this.engine.ctx;
    if (!ctx) return null;
    const hit = this.waves.get(kind);
    if (hit) return hit;
    const duty = kind === 'pulse25' ? 0.25 : 0.125;
    const n = 32;
    const real = new Float32Array(n);
    const imag = new Float32Array(n);
    for (let k = 1; k < n; k++) {
      real[k] = (2 / (k * Math.PI)) * Math.sin(k * Math.PI * duty);
    }
    const wave = ctx.createPeriodicWave(real, imag);
    this.waves.set(kind, wave);
    return wave;
  }

  private voice(
    kind: Song['leadWave'] | 'triangle',
    freq: number,
    t: number,
    dur: number,
    gain: number,
    eerie: boolean,
  ): void {
    const ctx = this.engine.ctx;
    if (!ctx) return;
    const osc = ctx.createOscillator();
    if (kind === 'pulse25' || kind === 'pulse12') {
      const w = this.wave(kind);
      if (w) osc.setPeriodicWave(w);
    } else {
      osc.type = kind === 'square' ? 'square' : 'triangle';
    }
    osc.frequency.setValueAtTime(freq, t);
    const g = ctx.createGain();
    const end = t + Math.max(0.05, dur);
    g.gain.setValueAtTime(0.0001, t);
    g.gain.exponentialRampToValueAtTime(gain, t + (eerie ? 0.2 : 0.008));
    g.gain.setTargetAtTime(gain * 0.6, t + 0.05, 0.1);
    g.gain.exponentialRampToValueAtTime(0.0001, end + (eerie ? 0.4 : 0.03));
    if (dur > 0.25 || eerie) {
      const lfo = ctx.createOscillator();
      const depth = ctx.createGain();
      lfo.frequency.value = eerie ? 3 : 5.5;
      depth.gain.value = freq * (eerie ? 0.02 : 0.006);
      lfo.connect(depth).connect(osc.frequency);
      lfo.start(t + 0.1);
      lfo.stop(end + 0.5);
    }
    if (eerie) osc.detune.setValueAtTime(-18, t);
    osc.connect(g).connect(this.engine.musicBus);
    osc.start(t);
    osc.stop(end + 0.5);
  }

  private drum(token: string, t: number): void {
    const e = this.engine;
    if (token.includes('k'))
      e.tone({
        type: 'sine',
        freq: 150,
        endFreq: 45,
        at: t,
        dur: 0.14,
        gain: 0.5,
        bus: e.musicBus,
      });
    if (token.includes('s')) {
      e.hiss({
        at: t,
        dur: 0.13,
        gain: 0.22,
        filter: 'bandpass',
        freq: 1900,
        q: 0.8,
        bus: e.musicBus,
      });
      e.tone({
        type: 'triangle',
        freq: 190,
        endFreq: 140,
        at: t,
        dur: 0.06,
        gain: 0.12,
        bus: e.musicBus,
      });
    }
    if (token.includes('h'))
      e.hiss({ at: t, dur: 0.035, gain: 0.09, filter: 'highpass', freq: 7500, bus: e.musicBus });
    if (token.includes('o'))
      e.hiss({ at: t, dur: 0.14, gain: 0.08, filter: 'highpass', freq: 6500, bus: e.musicBus });
  }
}
